import { memo, useState, useCallback, useEffect } from 'react'
import { PlusIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../context/AuthContext'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { vendorAPI } from '../../services/api'
import { toast } from '../../services/toastService'
import { createLogger } from '../../utils/logger'
import { baseInputClasses } from '../../styles/inputStyles'
import EntityOfficialMenu from '../map/EntityOfficialMenu'

const logger = createLogger('ExperiencesTab')

// Service categories — use neutral copy
const SERVICE_CATEGORIES = new Set([
  'service', 'cleaning', 'cleaner', 'care', 'education',
  'tutor', 'creative', 'trade', 'trades', 'mutual_aid',
])

/**
 * ExperiencesTab - Community "experiences" for an entity.
 *
 * Authenticated users can submit what they consumed (food item, service received).
 * The community list of all submitted offerings is shown below the form.
 * Unauthenticated users see a sign-in prompt in place of the form.
 *
 * @param {{ entityId: string, entityCategory: string, t: Function }} props
 * @atomic @idempotent
 */
const ExperiencesTab = memo(function ExperiencesTab({ entityId, entityCategory, t }) {
  const { isAuthenticated } = useAuth()
  const isService = SERVICE_CATEGORIES.has((entityCategory || '').toLowerCase())

  const placeholder = isService
    ? t('enrichment.placeholderService', 'Service you received (e.g. window cleaning)')
    : t('enrichment.placeholderFood', 'What you had (e.g. al pastor tacos)')

  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [communityItems, setCommunityItems] = useState([])
  const { execute, loading } = useAsyncOperation()

  // Load community offerings once
  useEffect(() => {
    vendorAPI.getMenu(entityId)
      .then(r => setCommunityItems(r.data?.offerings || r.data || []))
      .catch(err => logger.warn('Failed to load offerings', err))
  }, [entityId])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    await execute(
      () => vendorAPI.submitOffering(entityId, {
        offeringName: name.trim(),
        price: price.trim() ? parseFloat(price) : undefined,
      }),
      {
        onSuccess: (res) => {
          toast.success(t('enrichment.itemAdded', 'Added to community experiences!'))
          setName('')
          setPrice('')
          // Optimistically add to list
          const added = res?.data?.offering
          if (added) {
            setCommunityItems(prev => [{ name: added.offeringName || name.trim(), count: 1, price: added.price }, ...prev])
          } else {
            // Refetch
            vendorAPI.getMenu(entityId)
              .then(r => setCommunityItems(r.data?.offerings || r.data || []))
              .catch(() => {})
          }
        },
        onError: (err) => {
          logger.error('Failed to submit offering', err)
          const code = err?.response?.data?.error?.code
          if (code === 'proof_of_presence_required') {
            toast.error(t('enrichment.popRequired', 'Spot or confirm this entity first before sharing an experience'))
          } else {
            toast.error(t('enrichment.submitError', 'Could not add experience. Please try again.'))
          }
        },
      }
    )
  }, [entityId, execute, name, price, t])

  return (
    <div className="pb-4">
      <EntityOfficialMenu entityId={entityId} t={t} />

      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        {t('enrichment.tabTitle', 'Community Experiences')}
      </h3>

      {/* Submit form — auth required */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="mb-4 space-y-2">
          <input
            id="experience-name"
            name="experienceName"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={placeholder}
            maxLength={120}
            autoComplete="off"
            className={`${baseInputClasses} text-sm`}
          />
          <div className="flex gap-2">
            <input
              id="experience-price"
              name="experiencePrice"
              type="number"
              value={price}
              onChange={e => setPrice(e.target.value)}
              placeholder={t('enrichment.pricePlaceholder', 'Price (optional)')}
              min="0"
              step="0.01"
              className={`${baseInputClasses} text-sm w-32`}
            />
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
              {t('enrichment.addButton', 'Add')}
            </button>
          </div>
        </form>
      ) : (
        <p className="text-sm text-gray-500 italic mb-4">
          {t('enrichment.signInToAdd', 'Sign in to share your experience.')}
        </p>
      )}

      {/* Community list */}
      {communityItems.length > 0 ? (
        <ul className="space-y-1.5">
          {communityItems.map((item, i) => (
            <li key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
              <span className="flex items-center gap-1.5 text-gray-800">
                <CheckIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                {item.name || item.offeringName}
              </span>
              <span className="text-gray-400 text-xs tabular-nums">
                {item.price != null ? `$${Number(item.price).toFixed(2)}` : ''}
                {item.count != null && item.count > 1
                  ? ` · ${item.count}×`
                  : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-400 italic">
          {t('enrichment.noExperiences', 'No experiences shared yet. Be the first!')}
        </p>
      )}
    </div>
  )
})

export default ExperiencesTab
